import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  CircularProgress,
  Typography,
  Button,
  Select,
  MenuItem,
  Grid,
  Tooltip,
} from "@mui/material";
import OfflinePinIcon from "@mui/icons-material/OfflinePin";
import WarningIcon from "@mui/icons-material/Warning";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import useAxiosPrivate from "../../common/useAxiosPrivate";
import { toast } from "react-toastify";
import { responsibleOptions } from "./responsibleOptions";
import { IUserInfo } from "../../store/actions";
import { useSelector } from "react-redux";

export interface ReworkProps {
  chassisNo: string;
  model: string;
  setModel: (model: string) => void;
}

interface ReworkItem {
  itemNo: number;
  problemDescription: string;
  whoFoundFault: string;
  responsible: string;
  whoDidRework: string;
  timeSpentOnRework: {
    hours: string;
    minutes: string;
    seconds: string;
  };
  completed: boolean;
  comment?: string | null;
}

const stationTitleMap: Record<string, string> = {
  "incoming-chassis-inspection": "Incoming Chassis Rework",
  qc_electrical_chassis_prep: "Electrical Chassis Prep Rework",
  "furniture-inspection": "Furniture Rework",
  "floor-inspection": "Floor Rework",
  "side-wall-inspection": "Side Walls Rework",
  "roof-inspection": "Roof Rework",
  "j-mould-inspection": "J. Mould Rework",
  "building-inspection": "Building Rework",
  "post-building-inspection": "Post Building Rework",
  "electrical-bms-inspection": "Electrical BMS Rework",
  "electrical-fit-off": "Electrical Fit-Off Rework",
  "electrical-inspection": "Electrical Rework",
  "qa-quality-inspection": "QA Rework",
  qc_plumbing_rework: "Plumbing Rework",
  "finishing-quality-inspection": "Finishing Rework",
  "door-drawer-quality-inspection": "Doors, Drawers and Benchtop Rework",
  qa_green_tag_rework: "QA Green Tag Rework",
  "sealing-quality-inspection": "Sealing Rework",
  "compliance-quality-inspection": "Compliance Rework",
  "final-electrical-inspection": "Final Electrical Test Rework",
  "cleaning-quality-inspection": "Cleaning Rework",
  "gp-quality-inspection": "QUALITY CONTROL INSPECTION Rework",
};

const ReworkSummary: React.FC<ReworkProps> = ({
  chassisNo,
  model,
  setModel,
}) => {
  const axiosPrivate = useAxiosPrivate();
  const [loading, setLoading] = useState(false);
  const [reworkData, setReworkData] = useState<any[]>([]);
  const hasUnsavedChanges = chassisNo;

  const authInfo: IUserInfo = useSelector(
    (state: any) => state.userAuthInfoState
  );
  const userRole = authInfo.roles;
  const isElectricalQC = userRole?.includes("electrical-qc");

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const fetchReworkData = async () => {
      setLoading(true);
      try {
        const response = await axiosPrivate.post(`get_all_rework/${chassisNo}`);
        const data = response.data || [];

        if (data.length > 0) {
          setModel(data[0].model);
        } else {
          setModel("");
        }

        const allStations = Object.keys(stationTitleMap).map((stationKey) => ({
          station_name: stationKey,
          stationTitle: stationTitleMap[stationKey],
          reworkItems: [],
        }));

        const mergedData = allStations.map((station) => {
          const stationData = data.find(
            (item: { station_name: string }) =>
              item.station_name === station.station_name
          );
          return stationData || station;
        });

        setReworkData(mergedData);
      } catch (error) {
        console.error("Error fetching rework data:", error);
        toast.error("Failed to fetch rework data.");
      } finally {
        setLoading(false);
      }
    };

    if (chassisNo) fetchReworkData();
  }, [chassisNo, axiosPrivate, setModel]);

  const handleFieldChange = (
    stationIndex: number,
    itemIndex: number,
    field: string,
    value: string | boolean
  ) => {
    setReworkData((prev) =>
      prev.map((station, sIndex) =>
        sIndex === stationIndex
          ? {
              ...station,
              reworkItems: station.reworkItems.map(
                (item: any, iIndex: number) =>
                  iIndex === itemIndex ? { ...item, [field]: value } : item
              ),
            }
          : station
      )
    );
  };

  const handleTimeChange = (
    stationIndex: number,
    itemIndex: number,
    timeField: "hours" | "minutes",
    value: string
  ) => {
    const numericValue = parseInt(value || "0", 10);

    if (
      (timeField === "hours" && numericValue < 0) ||
      (timeField === "minutes" && (numericValue < 0 || numericValue > 59))
    ) {
      toast.error("Minutes must be between 0 and 59.");
      return;
    }

    setReworkData((prev) =>
      prev.map((station, sIndex) =>
        sIndex === stationIndex
          ? {
              ...station,
              reworkItems: station.reworkItems.map(
                (item: { timeSpentOnRework: any }, iIndex: number) =>
                  iIndex === itemIndex
                    ? {
                        ...item,
                        timeSpentOnRework: {
                          ...item.timeSpentOnRework,
                          [timeField]: value,
                        },
                      }
                    : item
              ),
            }
          : station
      )
    );
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "N/A";
    const [year, month, day] = dateStr.split("-");
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return `${day} ${monthNames[parseInt(month, 10) - 1]} ${year}`;
  };

  const saveData = async () => {
    setLoading(true);
    try {
      if (reworkData.length === 0) {
        toast.error("No rework data to save.");
        return;
      }

      let isSaved = false;

      const savePromises = reworkData.map(async (station) => {
        if (!station.reworkItems || station.reworkItems.length === 0) {
          return;
        }

        const payload = {
          chassisNo,
          station_name: station.station_name,
          model: station.model,
          reworkItems: station.reworkItems.map(
            (item: {
              itemNo: any;
              problemDescription: any;
              comment: any;
              whoFoundFault: any;
              responsible: any;
              whoDidRework: any;
              timeSpentOnRework: { hours: any; minutes: any; seconds: any };
              completed: any;
            }) => ({
              itemNo: item.itemNo,
              problemDescription: item.problemDescription || undefined,
              comment: item.comment || null,
              whoFoundFault: item.whoFoundFault || null,
              responsible: item.responsible || null,
              whoDidRework: item.whoDidRework || null,
              timeSpentOnRework: {
                hours: parseInt(item.timeSpentOnRework?.hours || "0", 10),
                minutes: parseInt(item.timeSpentOnRework?.minutes || "0", 10),
                seconds: parseInt(item.timeSpentOnRework?.seconds || "0", 10),
              },
              completed: Boolean(item.completed),
            })
          ),
        };

        const reworkType =
          Object.entries(stationTitleMap).find(
            ([key]) => key === station.station_name
          )?.[0] ||
          station.station_name ||
          "unknown-rework-type";

        try {
          await axiosPrivate.post(`/rework/save`, payload, {
            params: { rework_type: reworkType },
          });
          isSaved = true;
        } catch (error) {
          console.error("Error saving station data:", error);
          toast.error("Failed to save rework data.");
        }
      });

      await Promise.all(savePromises);

      if (isSaved) {
        toast.success("Rework data saved successfully!");
      } else {
        toast.error("No rework data was saved.");
      }
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Failed to save rework data.");
    } finally {
      setLoading(false);
    }
  };

  const formatProblemDescription = (description: string): React.ReactNode => {
    if (!description) return null;

    const lastColonIndex = description.lastIndexOf(":");
    if (lastColonIndex === -1) {
      return (
        <Typography sx={{ fontSize: "16px", color: "rgba(0, 0, 0, 0.38)" }}>
          {description}
        </Typography>
      );
    }

    const beforeColon = description.slice(0, lastColonIndex + 1);
    const afterColon = description.slice(lastColonIndex + 1);

    return (
      <>
        <Typography
          component="span"
          sx={{ fontSize: "16px", color: "rgba(0, 0, 0, 0.38)" }}
        >
          {beforeColon}
        </Typography>
        <Typography
          component="span"
          sx={{
            fontSize: "16px",
            fontWeight: "bold",
            color: "rgba(0, 0, 0, 0.38)",
          }}
        >
          {afterColon}
        </Typography>
      </>
    );
  };

  const isReworkIncomplete = (reworkItems: ReworkItem[]) => {
    if (reworkItems.length === 0) return true;
    return !reworkItems.every((item) => item.completed === true);
  };

  return (
    <>
      <div>
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "10vh",
            }}
          >
            <CircularProgress />
          </div>
        ) : reworkData.length === 0 && chassisNo !== "" ? (
          <Typography
            variant="h6"
            sx={{
              textAlign: "center",
              marginTop: "20px",
              color: "gray",
              fontWeight: "bold",
            }}
          >
            No rework data is available for the provided DC number.
          </Typography>
        ) : (
          reworkData.map((station, stationIndex) => {
            const reworkIncomplete = isReworkIncomplete(station.reworkItems);

            return (
              <div key={stationIndex} style={{ marginBottom: "1em" }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    color: "white",
                    textAlign: "center",
                    backgroundColor: "#3B1150",
                    padding: "8px",
                    marginBottom: "0",
                    fontWeight: "bold",
                  }}
                >
                  {stationTitleMap[station.station_name] ||
                    station.station_name}
                </Typography>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow
                        sx={{
                          backgroundColor: "#F8F8F8",
                          padding: "10px",
                          boxShadow: "0px 2px 5px rgba(0,0,0,0.1)",
                        }}
                      >
                        <TableCell
                          sx={{
                            width: "33.33%",
                            fontSize: "16px",
                            fontWeight: "bold",
                            padding: "10px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "8px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <Typography
                                component="span"
                                sx={{
                                  color: "#005EB8",
                                  fontWeight: "bold",
                                  fontSize: "16px",
                                  minWidth: "90px",
                                }}
                              >
                                Checklist:
                              </Typography>
                              {station.completeStatus === "Complete" ? (
                                <>
                                  <OfflinePinIcon sx={{ color: "#2E7D32" }} />
                                  <Typography
                                    component="span"
                                    sx={{
                                      color: "#2E7D32",
                                      fontWeight: "bold",
                                      fontSize: "16px",
                                    }}
                                  >
                                    Complete
                                  </Typography>
                                </>
                              ) : (
                                <>
                                  <WarningIcon sx={{ color: "#F57C00" }} />
                                  <Typography
                                    component="span"
                                    sx={{
                                      color: "#C62828",
                                      fontWeight: "bold",
                                      fontSize: "16px",
                                    }}
                                  >
                                    Incomplete
                                  </Typography>
                                </>
                              )}
                            </div>
                            {station.reworkItems.length > 0 && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                <Typography
                                  component="span"
                                  sx={{
                                    color: "#005EB8",
                                    fontWeight: "bold",
                                    fontSize: "16px",
                                    minWidth: "90px",
                                  }}
                                >
                                  Rework:
                                </Typography>
                                {reworkIncomplete ? (
                                  <>
                                    <WarningIcon sx={{ color: "#F57C00" }} />
                                    <Typography
                                      component="span"
                                      sx={{
                                        color: "#C62828",
                                        fontWeight: "bold",
                                        fontSize: "16px",
                                      }}
                                    >
                                      Incomplete
                                    </Typography>
                                  </>
                                ) : (
                                  <>
                                    <OfflinePinIcon sx={{ color: "#2E7D32" }} />
                                    <Typography
                                      component="span"
                                      sx={{
                                        color: "#2E7D32",
                                        fontWeight: "bold",
                                        fontSize: "16px",
                                      }}
                                    >
                                      Complete
                                    </Typography>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell
                          sx={{
                            width: "33.33%",
                            textAlign: "center",
                            fontSize: "16px",
                            fontWeight: "bold",
                            color: "#005EB8",
                            padding: "10px",
                            verticalAlign: "top",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "8px",
                            }}
                          >
                            <PersonIcon sx={{ fontSize: "20px" }} />
                            <span>Checked by:</span>
                            <span
                              style={{ fontStyle: "italic", color: "#424242" }}
                            >
                              {station.checkedBy || "N/A"}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell
                          sx={{
                            width: "33.33%",
                            fontSize: "16px",
                            fontWeight: "bold",
                            color: "#005EB8",
                            textAlign: "right",
                            padding: "10px",
                            verticalAlign: "top",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              gap: "8px",
                            }}
                          >
                            <CalendarTodayIcon sx={{ fontSize: "20px" }} />
                            <span
                              style={{ fontStyle: "italic", color: "#424242" }}
                            >
                              {formatDate(station.Date)}
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                  </Table>

                  {station.reworkItems.length > 0 && (
                    <Table>
                      <TableHead>
                        <TableRow className="table__head_row">
                          <TableCell
                            className="table__head_cell"
                            rowSpan={2}
                            align="center"
                            sx={{
                              color: "black !important",
                              fontWeight: "bold",
                              backgroundColor: "#C9B6D0",
                              border: "1px solid white",
                              width: "40%",
                            }}
                          >
                            <Typography
                              sx={{ fontSize: "16px", fontWeight: "bold" }}
                            >
                              Problem / Description
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                fontStyle: "italic",
                                textDecoration: "underline",
                              }}
                            >
                              ensure items detailed with fault type & location
                            </Typography>
                          </TableCell>
                          <TableCell
                            className="table__head_cell"
                            rowSpan={2}
                            align="center"
                            sx={{
                              fontWeight: "bold",
                              border: "1px solid white",
                              width: "10%",
                              color: "black !important",
                              backgroundColor: "#C9B6D0",
                            }}
                          >
                            Who Found Fault
                          </TableCell>
                          <TableCell
                            className="table__head_cell"
                            rowSpan={2}
                            align="center"
                            sx={{
                              fontWeight: "bold",
                              border: "1px solid white",
                              width: "10%",
                              color: "black !important",
                              backgroundColor: "#C9B6D0",
                            }}
                          >
                            Responsible
                          </TableCell>
                          <TableCell
                            className="table__head_cell"
                            colSpan={3}
                            align="center"
                            sx={{
                              fontWeight: "bold",
                              border: "1px solid white",
                              width: "10%",
                              color: "black !important",
                              backgroundColor: "#C9B6D0",
                            }}
                          >
                            Completed
                          </TableCell>
                        </TableRow>

                        <TableRow className="table__head_row">
                          <TableCell
                            className="table__head_cell"
                            align="center"
                            sx={{
                              fontWeight: "bold",
                              border: "1px solid white",
                              width: "10%",
                              color: "black !important",
                              backgroundColor: "#C9B6D0",
                            }}
                          >
                            Who Did Rework
                          </TableCell>
                          <TableCell
                            className="table__head_cell"
                            align="center"
                            sx={{
                              fontWeight: "bold",
                              border: "1px solid white",
                              width: "5%",
                              color: "black !important",
                              backgroundColor: "#C9B6D0",
                            }}
                          >
                            ✔
                          </TableCell>
                          <TableCell
                            className="table__head_cell"
                            align="center"
                            sx={{
                              fontWeight: "bold",
                              border: "1px solid white",
                              width: "10%",
                              color: "black !important",
                              backgroundColor: "#C9B6D0",
                            }}
                          >
                            Time Spent on Rework
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {station.reworkItems.map(
                          (item: ReworkItem, itemIndex: number) => (
                            <TableRow key={itemIndex}>
                              <TableCell
                                sx={{ borderLeft: "1px solid #d0d0d0" }}
                              >
                                <TextField
                                  variant="standard"
                                  onChange={(e) =>
                                    handleFieldChange(
                                      stationIndex,
                                      itemIndex,
                                      "problemDescription",
                                      e.target.value
                                    )
                                  }
                                  fullWidth
                                  multiline
                                  disabled={!isElectricalQC}
                                  InputProps={{
                                    disableUnderline: true,
                                    style: {
                                      fontSize: "16px",
                                    },
                                  }}
                                  sx={{
                                    "& .MuiInputBase-input": {
                                      padding: 0,
                                    },
                                    "& .MuiInputBase-root": {
                                      border: "none",
                                      backgroundColor: "transparent",
                                    },
                                  }}
                                />
                                <div>
                                  {formatProblemDescription(
                                    item.problemDescription
                                  )}
                                  {item.comment &&
                                    item.comment.trim() !== "" && (
                                      <Typography
                                        sx={{
                                          fontSize: "16px",
                                          color: "rgba(0, 0, 0, 0.38)",
                                          marginTop: "4px",
                                        }}
                                      >
                                        <Typography
                                          component="span"
                                          sx={{
                                            textDecoration: "underline",
                                            fontSize: "16px",
                                            color: "rgba(0, 0, 0, 0.38)",
                                          }}
                                        >
                                          Comment
                                        </Typography>
                                        : {item.comment}
                                      </Typography>
                                    )}
                                </div>
                              </TableCell>
                              <TableCell
                                sx={{ borderLeft: "1px solid #d0d0d0" }}
                              >
                                <TextField
                                  variant="standard"
                                  value={item.whoFoundFault}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      stationIndex,
                                      itemIndex,
                                      "whoFoundFault",
                                      e.target.value
                                    )
                                  }
                                  disabled={!isElectricalQC}
                                  fullWidth
                                  InputProps={{
                                    disableUnderline: true,
                                    style: {
                                      fontSize: "16px",
                                    },
                                  }}
                                  sx={{
                                    "& .MuiInputBase-input": {
                                      padding: 0,
                                    },
                                    "& .MuiInputBase-root": {
                                      border: "none",
                                      backgroundColor: "transparent",
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ borderLeft: "1px solid #d0d0d0" }}
                              >
                                <Select
                                  value={item.responsible || ""}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      stationIndex,
                                      itemIndex,
                                      "responsible",
                                      e.target.value
                                    )
                                  }
                                  variant="standard"
                                  fullWidth
                                  displayEmpty
                                  disabled={!isElectricalQC}
                                  sx={{
                                    "& .MuiSelect-select": {
                                      padding: "8px",
                                      fontSize: "16px",
                                    },
                                    "&:before, &:after": {
                                      borderBottom: "none !important",
                                    },
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      border: "none",
                                    },
                                    backgroundColor: "transparent",
                                  }}
                                >
                                  {responsibleOptions.map((option) => (
                                    <MenuItem key={option} value={option}>
                                      {option}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </TableCell>
                              <TableCell
                                sx={{ borderLeft: "1px solid #d0d0d0" }}
                              >
                                <TextField
                                  disabled={!isElectricalQC}
                                  variant="standard"
                                  value={item.whoDidRework}
                                  onChange={(e) =>
                                    handleFieldChange(
                                      stationIndex,
                                      itemIndex,
                                      "whoDidRework",
                                      e.target.value
                                    )
                                  }
                                  fullWidth
                                  InputProps={{
                                    disableUnderline: true,
                                    style: {
                                      fontSize: "16px",
                                    },
                                    disabled: !isElectricalQC,
                                  }}
                                  sx={{
                                    "& .MuiInputBase-input": {
                                      padding: 0,
                                    },
                                    "& .MuiInputBase-root": {
                                      border: "none",
                                      backgroundColor: "transparent",
                                    },
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ borderLeft: "1px solid #d0d0d0" }}
                              >
                                <Tooltip
                                  title={
                                    !item.whoDidRework || !item.responsible
                                      ? "'Who Did Rework' and 'Responsible' must be filled to enable the Checkbox."
                                      : ""
                                  }
                                  arrow
                                  disableHoverListener={
                                    !!item.whoDidRework && !!item.responsible
                                  }
                                >
                                  <span>
                                    <Checkbox
                                      checked={item.completed}
                                      disabled={
                                        !item.whoDidRework || !item.responsible
                                      }
                                      onChange={(e) =>
                                        handleFieldChange(
                                          stationIndex,
                                          itemIndex,
                                          "completed",
                                          e.target.checked
                                        )
                                      }
                                      sx={{
                                        opacity:
                                          !item.whoDidRework ||
                                          !item.responsible
                                            ? 0.5
                                            : 1,
                                        filter:
                                          !item.whoDidRework ||
                                          !item.responsible
                                            ? "blur(1px)"
                                            : "none",
                                      }}
                                    />
                                  </span>
                                </Tooltip>
                              </TableCell>

                              <TableCell
                                sx={{
                                  justifyContent: "center",
                                  alignItems: "center",
                                  borderLeft: "1px solid #d0d0d0",
                                  textAlign: "center",
                                  borderRight: "1px solid #d0d0d0",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  <TextField
                                    disabled={!isElectricalQC}
                                    variant="standard"
                                    placeholder="HH"
                                    value={item.timeSpentOnRework.hours || ""}
                                    onChange={(e) =>
                                      handleTimeChange(
                                        stationIndex,
                                        itemIndex,
                                        "hours",
                                        e.target.value
                                      )
                                    }
                                    size="small"
                                    sx={{
                                      width: "2em",
                                      textAlign: "center",
                                      fontSize: "16px",
                                    }}
                                  />
                                  <TextField
                                    disabled={!isElectricalQC}
                                    variant="standard"
                                    placeholder="MM"
                                    value={item.timeSpentOnRework.minutes || ""}
                                    onChange={(e) =>
                                      handleTimeChange(
                                        stationIndex,
                                        itemIndex,
                                        "minutes",
                                        e.target.value
                                      )
                                    }
                                    size="small"
                                    sx={{
                                      width: "2em",
                                      textAlign: "center",
                                      fontSize: "16px",
                                    }}
                                  />
                                </div>
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  )}
                </TableContainer>
              </div>
            );
          })
        )}
      </div>
      {isElectricalQC && (
        <Grid container justifyContent="center" mt={2} spacing={2}>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={saveData}
              disabled={loading || reworkData.length === 0}
            >
              Save
            </Button>
          </Grid>
        </Grid>
      )}
    </>
  );
};
export default ReworkSummary;
